/**
 * Tulola Wishlist — no app, no login, stored on the device.
 *
 * Storage is a single localStorage key holding product handles. Handles (not IDs)
 * because the Ajax API endpoint /products/<handle>.js is the only way to read fresh
 * price and stock without a Storefront API token, and it keys on the handle.
 *
 * Everything a visitor sees on the wishlist page is fetched at render time, so a
 * product that sold out or changed price after being saved never shows stale data.
 *
 * Public surface: window.TulolaWishlist
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tulola_wishlist_v1';
  var CACHE_PREFIX = 'tulola_wl_product_';
  var CACHE_TTL = 5 * 60 * 1000; // product JSON is cheap to refetch; 5 min is enough to survive a page hop
  var CHANGE_EVENT = 'tulola:wishlist:change';
  var MAX_ITEMS = 100; // guards against an unbounded share URL and a runaway localStorage entry
  var FETCH_CONCURRENCY = 6;
  var TOAST_DURATION = 4000; // long enough to read and reach Undo, short enough not to nag
  var POP_DURATION = 220;

  var settings = (window.theme && window.theme.wishlist) || {};
  var pageUrl = settings.pageUrl || '/pages/wishlist';

  /* ------------------------------------------------------------------ *
   * Storage
   * ------------------------------------------------------------------ */

  // Private-mode Safari and blocked-storage browsers throw on write. Falling back to
  // memory keeps every control working for the session instead of throwing on click.
  var memoryFallback = null;

  function readRaw() {
    if (memoryFallback) return memoryFallback;
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { v: 1, items: [] };
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return { v: 1, items: [] };
      return parsed;
    } catch (e) {
      return { v: 1, items: [] };
    }
  }

  function writeRaw(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      memoryFallback = null;
    } catch (e) {
      memoryFallback = data;
    }
  }

  function items() {
    return readRaw().items.filter(function (item) {
      return item && typeof item.h === 'string' && item.h.length;
    });
  }

  function handles() {
    return items().map(function (item) {
      return item.h;
    });
  }

  function has(handle) {
    return handles().indexOf(handle) !== -1;
  }

  // savedAt is only passed when undoing a removal: restoring the original timestamp
  // puts the product back where it was in the list instead of jumping it to the top.
  function add(handle, savedAt) {
    if (!handle || has(handle)) return false;
    var list = items();
    list.push({ h: handle, t: savedAt || Date.now() });
    // Newest first: the wishlist page reads top-down and recency is the useful order.
    list.sort(function (a, b) {
      return b.t - a.t;
    });
    writeRaw({ v: 1, items: list.slice(0, MAX_ITEMS) });
    announce('add', handle);
    return true;
  }

  function remove(handle) {
    if (!handle || !has(handle)) return false;
    writeRaw({
      v: 1,
      items: items().filter(function (item) {
        return item.h !== handle;
      })
    });
    announce('remove', handle);
    return true;
  }

  function savedAtOf(handle) {
    var found = items().filter(function (item) {
      return item.h === handle;
    })[0];
    return found ? found.t : null;
  }

  function toggle(handle) {
    return has(handle) ? (remove(handle), false) : (add(handle), true);
  }

  function clear() {
    writeRaw({ v: 1, items: [] });
    announce('clear', null);
  }

  function count() {
    return items().length;
  }

  function announce(action, handle) {
    syncButtons();
    syncCounters();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { action: action, handle: handle, count: count() } })
    );
  }

  /* ------------------------------------------------------------------ *
   * Share link — handles encoded into the URL so a wishlist can move
   * between devices without an account.
   * ------------------------------------------------------------------ */

  function encode(list) {
    var source = (list || handles()).join(',');
    if (!source) return '';
    try {
      // base64url: '+' and '/' are unsafe in a query string, '=' is noise
      return btoa(unescape(encodeURIComponent(source)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    } catch (e) {
      return '';
    }
  }

  function decode(token) {
    if (!token) return [];
    try {
      var base64 = token.replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) base64 += '=';
      return decodeURIComponent(escape(atob(base64)))
        .split(',')
        .filter(Boolean)
        .slice(0, MAX_ITEMS);
    } catch (e) {
      return [];
    }
  }

  function shareUrl() {
    var token = encode();
    if (!token) return '';
    return window.location.origin + pageUrl + '?w=' + token;
  }

  /* ------------------------------------------------------------------ *
   * Product data — Ajax API, cached briefly in sessionStorage
   * ------------------------------------------------------------------ */

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '');
  }

  function readCache(handle) {
    try {
      var raw = window.sessionStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > CACHE_TTL) return null;
      return entry.d;
    } catch (e) {
      return null;
    }
  }

  function writeCache(handle, data) {
    try {
      window.sessionStorage.setItem(CACHE_PREFIX + handle, JSON.stringify({ t: Date.now(), d: data }));
    } catch (e) {
      /* quota or blocked storage — the fetch still works, just uncached */
    }
  }

  function fetchProduct(handle) {
    var cached = readCache(handle);
    if (cached) return Promise.resolve(cached);

    return fetch(rootUrl() + '/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin'
    })
      .then(function (response) {
        // A deleted or unpublished product answers 404 — treat it as "gone", not an error
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (data) {
        if (data) writeCache(handle, data);
        return data;
      })
      .catch(function () {
        return null;
      });
  }

  // Small pool so a 40-item wishlist does not open 40 sockets at once.
  function fetchAll(list) {
    var results = new Array(list.length);
    var cursor = 0;

    function next() {
      if (cursor >= list.length) return Promise.resolve();
      var index = cursor++;
      return fetchProduct(list[index]).then(function (data) {
        results[index] = data;
        return next();
      });
    }

    var workers = [];
    for (var i = 0; i < Math.min(FETCH_CONCURRENCY, list.length); i++) workers.push(next());
    return Promise.all(workers).then(function () {
      return results;
    });
  }

  /* ------------------------------------------------------------------ *
   * Formatting
   * ------------------------------------------------------------------ */

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
    }
    // Fallback mirrors the shop's IDR format (no decimals, dot thousands)
    return 'Rp ' + Math.round(cents / 100).toLocaleString('id-ID');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (chr) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[chr];
    });
  }

  function resizeImage(url, size) {
    if (!url) return '';
    return url.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_' + size + '$1$2');
  }

  /* ------------------------------------------------------------------ *
   * Analytics — GA4 through GTM, per the project's dataLayer convention
   * ------------------------------------------------------------------ */

  function track(eventName, product) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (product) {
      payload.ecommerce = {
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'IDR',
        value: product.price ? product.price / 100 : undefined,
        items: [
          {
            item_id: String(product.id || ''),
            item_name: product.title || '',
            price: product.price ? product.price / 100 : undefined
          }
        ]
      };
    }
    payload.wishlist_count = count();
    window.dataLayer.push(payload);
  }

  /* ------------------------------------------------------------------ *
   * Buttons — server-rendered on theme cards, injected on app-rendered ones
   * ------------------------------------------------------------------ */

  var ICON = {
    outline:
      '<svg class="wishlist-btn__icon wishlist-btn__icon--outline" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M12 20.5 3.9 12.6a4.9 4.9 0 0 1 0-7 4.9 4.9 0 0 1 7 0l1.1 1.1 1.1-1.1a4.9 4.9 0 0 1 7 0 4.9 4.9 0 0 1 0 7z" ' +
      'fill="none" stroke="currentColor" stroke-width="1.6"/></svg>',
    filled:
      '<svg class="wishlist-btn__icon wishlist-btn__icon--filled" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M12 20.5 3.9 12.6a4.9 4.9 0 0 1 0-7 4.9 4.9 0 0 1 7 0l1.1 1.1 1.1-1.1a4.9 4.9 0 0 1 7 0 4.9 4.9 0 0 1 0 7z" ' +
      'fill="currentColor" stroke="currentColor" stroke-width="1.6"/></svg>'
  };

  function ariaLabelFor(label, title) {
    // "Save to wishlist: Cerita Red Crystal Ring" — twelve identical labels on a
    // collection page tell a screen-reader user nothing.
    return title ? (label || 'Save to wishlist') + ': ' + title : label || 'Save to wishlist';
  }

  function buttonMarkup(handle, variant, title) {
    var saved = has(handle);
    var label = saved ? settings.labelSaved : settings.labelSave;
    return (
      '<button type="button" class="wishlist-btn wishlist-btn--' +
      variant +
      (saved ? ' is-saved' : '') +
      '" data-wishlist-toggle data-wishlist-handle="' +
      escapeHtml(handle) +
      '" data-wishlist-title="' +
      escapeHtml(title || '') +
      '" aria-pressed="' +
      (saved ? 'true' : 'false') +
      '" aria-label="' +
      escapeHtml(ariaLabelFor(label, title)) +
      '">' +
      ICON.outline +
      ICON.filled +
      (variant === 'icon' ? '' : '<span class="wishlist-btn__label">' + escapeHtml(label) + '</span>') +
      '</button>'
    );
  }

  function syncButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('[data-wishlist-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var saved = has(button.getAttribute('data-wishlist-handle'));
      var label = saved ? settings.labelSaved : settings.labelSave;
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      if (label) button.setAttribute('aria-label', ariaLabelFor(label, button.getAttribute('data-wishlist-title')));
      var text = button.querySelector('.wishlist-btn__label');
      if (text && label) text.textContent = label;
    }
  }

  function syncCounters() {
    var total = count();
    var nodes = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = total > 0 ? String(total) : '';
      nodes[i].classList.toggle('wishlist-count--visible', total > 0);
    }
  }

  // Cards rendered by an app (Globo Filters) or by AJAX pagination never pass through
  // Liquid, so the button is injected here instead. The guard keeps server-rendered
  // cards from getting a second button.
  function hydrateCards(scope) {
    if (settings.showOnCards === false) return;
    var root = scope || document;
    // Same container the Liquid snippet renders into, so the guard below catches
    // server-rendered cards and no card ends up with two hearts.
    var cards = root.querySelectorAll('.grid-product__content');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector('.wishlist-btn')) continue;
      var anchor = card.querySelector('a[href*="/products/"]');
      if (!anchor) continue;
      var match = anchor.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) continue;
      var titleEl = card.querySelector('.toto-grid-product-title, .grid-product__title, h3');
      var title = titleEl ? titleEl.textContent.trim() : '';
      card.insertAdjacentHTML('afterbegin', buttonMarkup(decodeURIComponent(match[1]), 'icon', title));
    }
  }

  /* ------------------------------------------------------------------ *
   * Confirmation toast
   *
   * A saved product should confirm itself without stealing the page, and a
   * mis-tap should be one button away from undone — not a hunt through the
   * wishlist page. The toast therefore carries both a way forward (View) and a
   * way back (Undo), announces itself to screen readers, and waits while it is
   * being read instead of vanishing on a fixed timer.
   * ------------------------------------------------------------------ */

  var toastEl = null;
  var toastTimer = null;
  var toastUndo = null;
  var toastOrigin = null;

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function buildToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'wishlist-toast';
    toastEl.id = 'WishlistToast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.setAttribute('aria-atomic', 'true');
    toastEl.hidden = true;
    toastEl.innerHTML =
      '<span class="wishlist-toast__icon" aria-hidden="true">' + ICON.filled + '</span>' +
      '<span class="wishlist-toast__text"></span>' +
      '<span class="wishlist-toast__actions">' +
      '<a class="wishlist-toast__action" href="' + escapeHtml(pageUrl) + '" data-toast-view></a>' +
      '<button type="button" class="wishlist-toast__action wishlist-toast__action--undo" data-toast-undo></button>' +
      '</span>';
    document.body.appendChild(toastEl);

    // Hovering or tabbing in means the visitor is still reading — stop the clock.
    toastEl.addEventListener('mouseenter', pauseToast);
    toastEl.addEventListener('focusin', pauseToast);
    toastEl.addEventListener('mouseleave', startToastTimer);
    toastEl.addEventListener('focusout', startToastTimer);
    return toastEl;
  }

  // The theme keeps a sticky bar pinned to the bottom (Add to cart / View cart).
  // Measuring it each time is cheaper than guessing, and keeps the toast from
  // ever covering the primary call to action.
  function bottomObstacleHeight() {
    var bars = document.querySelectorAll('.sticky-atc, .sticky-add-to-cart, .sticky-view-cart');
    var tallest = 0;
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var style = window.getComputedStyle(bar);
      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) continue;
      var rect = bar.getBoundingClientRect();
      var sitsAtBottom = rect.height > 0 && rect.bottom >= window.innerHeight - 8;
      if (sitsAtBottom && rect.height > tallest) tallest = rect.height;
    }
    return tallest;
  }

  function positionToast() {
    if (!toastEl) return;
    toastEl.style.bottom = bottomObstacleHeight() + 20 + 'px';
  }

  function pauseToast() {
    window.clearTimeout(toastTimer);
  }

  function startToastTimer() {
    pauseToast();
    toastTimer = window.setTimeout(hideToast, TOAST_DURATION);
  }

  function showToast(options) {
    var el = buildToast();
    el.querySelector('.wishlist-toast__text').textContent = options.message || '';

    var view = el.querySelector('[data-toast-view]');
    view.textContent = settings.labelView || 'View';
    view.hidden = !options.showView;

    var undo = el.querySelector('[data-toast-undo]');
    undo.textContent = settings.labelUndo || 'Undo';
    undo.hidden = !options.undo;

    toastUndo = options.undo || null;
    toastOrigin = options.origin || null;

    el.hidden = false;
    positionToast();
    void el.offsetWidth; // let the browser register the pre-transition state
    el.classList.add('is-visible');
    startToastTimer();
  }

  function hideToast() {
    pauseToast();
    if (!toastEl || toastEl.hidden) return;
    toastEl.classList.remove('is-visible');
    var el = toastEl;
    window.setTimeout(function () {
      if (!el.classList.contains('is-visible')) el.hidden = true;
    }, prefersReducedMotion() ? 0 : 250);
  }

  function runUndo() {
    if (typeof toastUndo !== 'function') return;
    var action = toastUndo;
    var origin = toastOrigin;
    toastUndo = null;
    action();
    hideToast();
    // Send focus back where the visitor was, so keyboard users are not dropped
    // at the top of the document.
    if (origin && document.contains(origin) && typeof origin.focus === 'function') origin.focus();
  }

  function popHeart(button) {
    if (!button || prefersReducedMotion()) return;
    button.classList.remove('is-popping');
    void button.offsetWidth;
    button.classList.add('is-popping');
    window.setTimeout(function () {
      button.classList.remove('is-popping');
    }, POP_DURATION + 60);
  }

  /* ------------------------------------------------------------------ *
   * Wishlist page
   * ------------------------------------------------------------------ */

  function currentPageList() {
    var params = new URLSearchParams(window.location.search);
    var shared = params.get('w');
    if (shared) {
      var list = decode(shared);
      if (list.length) return { list: list, shared: true };
    }
    return { list: handles(), shared: false };
  }

  function cardMarkup(product, shared) {
    var url = rootUrl() + '/products/' + product.handle;
    var image = product.featured_image
      ? resizeImage(product.featured_image, '540x')
      : (product.images && product.images[0] ? resizeImage(product.images[0], '540x') : '');
    var soldOut = !product.available;
    var priceVaries = product.price_varies;
    var price = priceVaries
      ? (settings.labelFrom || 'From') + ' ' + formatMoney(product.price_min)
      : formatMoney(product.price);
    // One variant that can be bought goes straight to the cart; anything else needs a
    // choice, so the visitor is sent to the product page instead of guessing for them.
    var singleVariant = product.variants && product.variants.length === 1 ? product.variants[0] : null;
    var canAddDirectly = !soldOut && singleVariant && singleVariant.available;

    return (
      '<div class="wishlist-card" data-wishlist-card data-handle="' + escapeHtml(product.handle) + '">' +
      '<a class="wishlist-card__image-link" href="' + escapeHtml(url) + '">' +
      (image
        ? '<img class="wishlist-card__image" src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title) + '" loading="lazy">'
        : '<span class="wishlist-card__image wishlist-card__image--empty"></span>') +
      '</a>' +
      (shared
        ? ''
        : '<button type="button" class="wishlist-card__remove" data-wishlist-remove data-wishlist-handle="' +
          escapeHtml(product.handle) +
          '" aria-label="' + escapeHtml(settings.labelRemove || 'Remove') + '">&times;</button>') +
      '<div class="wishlist-card__body">' +
      '<a class="wishlist-card__title" href="' + escapeHtml(url) + '">' + escapeHtml(product.title) + '</a>' +
      '<div class="wishlist-card__price">' + price + '</div>' +
      (soldOut
        ? '<a class="btn btn--secondary wishlist-card__cta" href="' + escapeHtml(url) + '">' +
          escapeHtml(settings.labelSoldOut || 'Sold out — enquire') + '</a>'
        : canAddDirectly
        ? '<button type="button" class="btn wishlist-card__cta wishlist-card__cta--add" data-wishlist-add-to-cart data-variant-id="' +
          singleVariant.id + '">' + escapeHtml(settings.labelAddToCart || 'Add to cart') + '</button>'
        : '<a class="btn wishlist-card__cta" href="' + escapeHtml(url) + '">' +
          escapeHtml(settings.labelChoose || 'Choose options') + '</a>') +
      '</div>' +
      '</div>'
    );
  }

  function renderPage(container) {
    var grid = container.querySelector('[data-wishlist-grid]');
    var empty = container.querySelector('[data-wishlist-empty]');
    var toolbar = container.querySelector('[data-wishlist-toolbar]');
    var sharedNote = container.querySelector('[data-wishlist-shared-note]');
    if (!grid) return;

    var state = currentPageList();
    container.classList.toggle('wishlist-page--shared', state.shared);
    if (sharedNote) sharedNote.hidden = !state.shared;

    if (!state.list.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      if (toolbar) toolbar.hidden = true;
      return;
    }

    grid.setAttribute('aria-busy', 'true');
    fetchAll(state.list).then(function (products) {
      var found = products.filter(Boolean);

      // Products that 404 are gone from the catalogue for good — drop them from the
      // saved list so the visitor is not left with a permanently broken row.
      if (!state.shared) {
        var foundHandles = found.map(function (product) {
          return product.handle;
        });
        var stale = state.list.filter(function (handle) {
          return foundHandles.indexOf(handle) === -1;
        });
        if (stale.length) {
          writeRaw({
            v: 1,
            items: items().filter(function (item) {
              return stale.indexOf(item.h) === -1;
            })
          });
          syncCounters();
        }
      }

      grid.removeAttribute('aria-busy');
      grid.innerHTML = found
        .map(function (product) {
          return cardMarkup(product, state.shared);
        })
        .join('');

      if (empty) empty.hidden = found.length > 0;
      if (toolbar) toolbar.hidden = found.length === 0;
      track('view_wishlist', null);
    });
  }

  function addToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;
    var original = button.textContent;
    button.disabled = true;
    button.textContent = settings.labelAdding || 'Adding…';

    fetch(rootUrl() + '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('add failed');
        return response.json();
      })
      .then(function () {
        button.textContent = settings.labelAdded || 'Added';
        // Let the theme's ajax cart pick the change up; fall back to the cart page.
        if (window.jQuery && window.jQuery(document).trigger) {
          window.jQuery(document).trigger('ajaxProduct.afterCartLoad');
        }
        window.location.href = rootUrl() + '/cart';
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = original;
      });
  }

  function copyShareLink(button) {
    var url = shareUrl();
    if (!url) return;
    var done = function () {
      var original = button.getAttribute('data-label-default') || button.textContent;
      button.setAttribute('data-label-default', original);
      button.textContent = settings.labelCopied || 'Link copied';
      window.setTimeout(function () {
        button.textContent = original;
      }, 2500);
      track('share_wishlist', null);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, function () {
        window.prompt(settings.labelCopyManual || 'Copy this link:', url);
      });
    } else {
      window.prompt(settings.labelCopyManual || 'Copy this link:', url);
    }
  }

  function saveSharedList() {
    var state = currentPageList();
    if (!state.shared) return;
    state.list
      .slice()
      .reverse()
      .forEach(function (handle) {
        add(handle);
      });
    // Drop the token so a reload shows the visitor's own list, now containing these items
    window.location.href = pageUrl;
  }

  /* ------------------------------------------------------------------ *
   * Wiring
   * ------------------------------------------------------------------ */

  function refreshPageIfPresent() {
    var page = document.querySelector('[data-wishlist-page]');
    if (page) renderPage(page);
  }

  function onClick(event) {
    var toggleBtn = event.target.closest('[data-wishlist-toggle], [data-wishlist-remove]');
    if (toggleBtn) {
      event.preventDefault();
      event.stopPropagation(); // product cards wrap their content in a link
      var handle = toggleBtn.getAttribute('data-wishlist-handle');
      var isRemove = toggleBtn.hasAttribute('data-wishlist-remove');
      // Read the original save time before removing, so Undo can put the product
      // back in its old position rather than at the top of the list.
      var previousSavedAt = savedAtOf(handle);
      var nowSaved = isRemove ? (remove(handle), false) : toggle(handle);

      fetchProduct(handle).then(function (product) {
        track(nowSaved ? 'add_to_wishlist' : 'remove_from_wishlist', product);
      });

      if (nowSaved) {
        popHeart(toggleBtn);
        showToast({
          message: settings.labelToastSaved || 'Saved to wishlist',
          showView: true,
          undo: function () {
            remove(handle);
            refreshPageIfPresent();
          },
          origin: toggleBtn
        });
      } else {
        showToast({
          message: settings.labelToastRemoved || 'Removed from wishlist',
          showView: false,
          undo: function () {
            add(handle, previousSavedAt);
            refreshPageIfPresent();
          },
          origin: toggleBtn
        });
      }

      if (isRemove) {
        var card = toggleBtn.closest('[data-wishlist-card]');
        if (card) card.remove();
        var page = document.querySelector('[data-wishlist-page]');
        if (page && !page.querySelectorAll('[data-wishlist-card]').length) renderPage(page);
      }
      return;
    }

    if (event.target.closest('[data-toast-undo]')) {
      event.preventDefault();
      runUndo();
      return;
    }

    var cartBtn = event.target.closest('[data-wishlist-add-to-cart]');
    if (cartBtn) {
      event.preventDefault();
      addToCart(cartBtn);
      return;
    }

    var shareBtn = event.target.closest('[data-wishlist-share]');
    if (shareBtn) {
      event.preventDefault();
      copyShareLink(shareBtn);
      return;
    }

    var saveSharedBtn = event.target.closest('[data-wishlist-save-shared]');
    if (saveSharedBtn) {
      event.preventDefault();
      saveSharedList();
    }
  }

  function init() {
    document.addEventListener('click', onClick, true);

    // Escape dismisses the toast, the way every other transient thing behaves.
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') hideToast();
    });

    // The sticky bar comes and goes with scrolling, so re-measure while the
    // toast is on screen — it must never end up on top of Add to cart.
    window.addEventListener('resize', positionToast);
    window.addEventListener(
      'scroll',
      function () {
        if (toastEl && !toastEl.hidden) positionToast();
      },
      { passive: true }
    );

    hydrateCards();
    syncButtons();
    syncCounters();

    var page = document.querySelector('[data-wishlist-page]');
    if (page) renderPage(page);

    // App-rendered and AJAX-paginated grids arrive after load; watch for them cheaply.
    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        window.clearTimeout(pending);
        pending = window.setTimeout(function () {
          hydrateCards();
          syncButtons();
        }, 150);
      }).observe(document.body, { childList: true, subtree: true });
    }

    // Another tab changed the list — keep this one honest.
    window.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      memoryFallback = null;
      syncButtons();
      syncCounters();
      var current = document.querySelector('[data-wishlist-page]');
      if (current) renderPage(current);
    });
  }

  window.TulolaWishlist = {
    get: handles,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    clear: clear,
    count: count,
    encode: encode,
    decode: decode,
    shareUrl: shareUrl,
    refresh: function () {
      hydrateCards();
      syncButtons();
      syncCounters();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
